// archiveUtils.js
const NS_VALUE = -100;
const API_URL = process.env.REACT_APP_API_URL;

const formatValue = (val, decimals = 3) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === NS_VALUE || num === -1) return 'НС';
    return num.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatDateTimeWith24 = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    let hours = d.getHours();
    let displayDate = new Date(d);
    if (hours === 0) {
        displayDate.setDate(displayDate.getDate() - 1);
        hours = 24;
    }
    const day = String(displayDate.getDate()).padStart(2, '0');
    const month = String(displayDate.getMonth() + 1).padStart(2, '0');
    const year = displayDate.getFullYear();
    const hoursStr = String(hours).padStart(2, '0');
    return `${day}.${month}.${year} ${hoursStr}`;
};

const formatAccumEndDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString + 'T00:00:00');
    if (isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year} 24`;
};

const formatAddress = (device) => {
    if (!device) return 'Адрес не указан';
    if (device.address && device.address.trim()) return device.address.trim();
    const parts = [];
    if (device.city) parts.push(device.city.trim());
    if (device.settlement) parts.push(device.settlement.trim());
    if (device.district) parts.push(device.district.trim());
    if (device.street) parts.push(device.street.trim());
    if (device.houseNumber) parts.push(`дом ${device.houseNumber.trim()}`);
    const addr = parts.join(', ');
    return addr && addr !== 'дом ' ? addr : 'Адрес не указан';
};

const formatLocalDate = (d) => d.toLocaleDateString("en-CA");

const getDatePlusOne = (dateString) => {
    if (!dateString) return new Date();
    const d = new Date(dateString);
    d.setDate(d.getDate() + 1);
    return d;
};

const getAdjustedDateTo = (dateString, archiveType) => {
    if (!dateString) return formatLocalDate(new Date());
    const d = new Date(dateString + "T00:00:00");
    switch (archiveType) {
        case "day":
        case "hour":
            d.setDate(d.getDate() + 1);
            return formatLocalDate(d);
        case "month":
            d.setMonth(d.getMonth() + 1);
            return formatLocalDate(d);
        default:
            return dateString;
    }
};

const AdjustHOURforDate = (dateString) => {
    if (!dateString) return formatLocalDate(new Date());
    const d = new Date(dateString + "T00:00:00");
    d.setHours(d.getHours() + 1);
    return (
        formatLocalDate(d) + "T" +
        String(d.getHours()).padStart(2, "0") + ":" +
        String(d.getMinutes()).padStart(2, "0") + ":" +
        String(d.getSeconds()).padStart(2, "0")
    );
};

const getAdjustedDateFrom = (dateString, archiveType) => {
    switch (archiveType) {
        case "hour":
            return AdjustHOURforDate(dateString);
        default:
            return getAdjustedDateTo(dateString, archiveType);
    }
};

const getArchiveTypeName = (archiveType) => {
    switch (archiveType) {
        case 'hour': return 'Часовой';
        case 'day': return 'Суточный';
        case 'month': return 'Месячный';
        case 'integral': return 'Интегральный';
        default: return archiveType;
    }
};

// archiveUtils.js — обновлённая renderCellValue
const renderCellValue = (value, col) => {
    if (col.key === 'datetime') return value || '—';

    // Колонка НС — показываем число или галочку
    if (col.type === 'ns') {
        if (value === 0 || value === null || value === undefined) return '✓';
        return `⚠ ${value}`;
    }

    // Время (уже в часах после деления на 60)
    if (col.type === 'time') {
        return formatValue(value, 3);
    }

    const decimals = (col.unit?.includes('МПа') || col.unit?.includes('м³') || col.unit?.includes('т') || col.unit?.includes('Гкал')) ? 3 : (col.key.includes('time') ? 2 : 3);
    return formatValue(value, decimals);
};

const base64ToUint8Array = (base64) => {
    try {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    } catch (err) {
        console.error('Ошибка декодирования Base64:', err);
        return [];
    }
};

const switchConvert = (bitPosition, switchValue) => {
    if (switchValue === null || switchValue === undefined) return false;
    const num = parseInt(switchValue);
    if (isNaN(num)) return false;
    return (num & Math.pow(2, bitPosition)) === Math.pow(2, bitPosition);
};

// Определение типа устройства
const getDeviceType = (device) => {
    if (!device) return 'unknown';

    // Основное поле из API: deviceType = "Аксиома-03" | "Аксиома-01" | "Карат-307"
    const deviceType = (device.deviceType || '').toLowerCase().replace(/[\s\-_]/g, '');
    if (deviceType.includes('аксиома03') || deviceType.includes('aksioma03')) return 'aksioma03';
    if (deviceType.includes('аксиома01') || deviceType.includes('aksioma01')) return 'aksioma01';
    if (deviceType.includes('карат307') || deviceType.includes('karat307') || deviceType.includes('карат')) return 'karat307';

    // Fallback: по префиксу имени устройства
    const name = (device.name || '').toLowerCase();
    if (name.startsWith('ak_')) return 'aksioma03';
    if (name.startsWith('dev_')) return 'aksioma01';
    if (/^\d+$/.test(name)) return 'karat307';

    return 'unknown';
};

export {
    NS_VALUE, API_URL,
    formatValue, formatDateTimeWith24, formatAccumEndDate, formatAddress,
    formatLocalDate, getDatePlusOne, getAdjustedDateTo, getAdjustedDateFrom,
    getArchiveTypeName, renderCellValue, base64ToUint8Array, switchConvert,
    getDeviceType
};